import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  verifyMfa({ challengeId, code }) {
    return this.request('/auth/mfa/verify', { method: 'POST', body: { challengeId, code } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Registration ----
  registerAgency({ organizationName, contactPerson, email, mobileNumber, authLetterFilename }) {
    return this.request('/registrations', {
      method: 'POST',
      body: { organizationName, contactPerson, email, mobileNumber, authLetterFilename },
    });
  }

  // ---- Users (admin) ----
  listUsers({ limit = 10, offset = 0, search = '', status = '', role = '' } = {}) {
    const params = new URLSearchParams();
    params.set('limit', limit);
    params.set('offset', offset);
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (role) params.set('role', role);
    return this.requestWithMeta(`/users?${params.toString()}`);
  }

  createUser(body) {
    return this.request('/users', { method: 'POST', body });
  }

  updateUser(id, body) {
    return this.request(`/users/${id}`, { method: 'PUT', body });
  }

  setUserStatus(id, status) {
    return this.request(`/users/${id}/status`, { method: 'PATCH', body: { status } });
  }

  // ---- Contacts ----
  listContacts({ search = '', group = '', organization = '', limit = 25, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (group) params.set('group', group);
    if (organization) params.set('organization', organization);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/contacts?${params.toString()}`);
  }

  createContact(body) {
    return this.request('/contacts', { method: 'POST', body });
  }

  updateContact(id, body) {
    return this.request(`/contacts/${id}`, { method: 'PUT', body });
  }

  deleteContact(id) {
    return this.request(`/contacts/${id}`, { method: 'DELETE' });
  }

  bulkDeleteContacts(ids) {
    return this.request('/contacts/bulk-delete', { method: 'POST', body: { ids } });
  }

  addContactsToGroup(ids, groupName) {
    return this.request('/contacts/add-to-group', { method: 'POST', body: { ids, groupName } });
  }

  importContacts({ csv, groupName }) {
    return this.request('/contacts/import', { method: 'POST', body: { csv, groupName } });
  }

  listGroups() {
    return this.request('/contacts/groups');
  }

  createGroup(name) {
    return this.request('/contacts/groups', { method: 'POST', body: { name } });
  }

  // ---- Sender IDs ----
  listSenderIds({ status = 'All', search = '', limit = 15, offset = 0 } = {}) {
    const params = new URLSearchParams();
    params.set('status', status);
    if (search) params.set('search', search);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/sender-ids?${params.toString()}`);
  }

  senderIdStats() {
    return this.request('/sender-ids/stats');
  }

  createSenderId(body) {
    return this.request('/sender-ids', { method: 'POST', body });
  }

  approveSenderId(id) {
    return this.request(`/sender-ids/${id}/approve`, { method: 'POST' });
  }

  rejectSenderId(id, reason) {
    return this.request(`/sender-ids/${id}/reject`, { method: 'POST', body: { reason } });
  }

  // ---- Credits ----
  creditPool() {
    return this.request('/credits/pool');
  }

  creditAccounts() {
    return this.request('/credits/accounts');
  }

  allocateCredits(body) {
    return this.request('/credits/allocate', { method: 'POST', body });
  }

  listCreditTransactions({ accountId = '', search = '', limit = 15, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (accountId) params.set('accountId', accountId);
    if (search) params.set('search', search);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/credits/transactions?${params.toString()}`);
  }

  // ---- Personal credit tracking ----
  myCredit() {
    return this.request('/credits/me');
  }

  mySubscription() {
    return this.request('/credits/subscription');
  }

  updateCreditThreshold(threshold) {
    return this.request('/credits/me/threshold', { method: 'PUT', body: { threshold } });
  }

  myCreditTransactions({ type = '', from = '', to = '', limit = 15, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/credits/me/transactions?${params.toString()}`);
  }

  deductCredits({ amount, reason }) {
    return this.request('/credits/me/deduct', { method: 'POST', body: { amount, reason } });
  }

  // ---- Message Templates ----
  listTemplates({ search = '', category = '', limit = 10, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/templates?${params.toString()}`);
  }

  createTemplate(body) {
    return this.request('/templates', { method: 'POST', body });
  }

  updateTemplate(id, body) {
    return this.request(`/templates/${id}`, { method: 'PUT', body });
  }

  deleteTemplate(id) {
    return this.request(`/templates/${id}`, { method: 'DELETE' });
  }

  // ---- API Keys ----
  listApiKeys() {
    return this.request('/api-keys');
  }

  apiKeyLogs() {
    return this.request('/api-keys/logs');
  }

  createApiKey(body) {
    return this.request('/api-keys', { method: 'POST', body });
  }

  setApiKeyStatus(id, status) {
    return this.request(`/api-keys/${id}/status`, { method: 'PATCH', body: { status } });
  }

  rotateApiKey(id) {
    return this.request(`/api-keys/${id}/rotate`, { method: 'POST' });
  }

  revokeApiKey(id) {
    return this.request(`/api-keys/${id}`, { method: 'DELETE' });
  }

  // ---- Admin dashboard ----
  adminDashboard({ granularity = 'daily' } = {}) {
    return this.request(`/admin/dashboard?granularity=${granularity}`);
  }

  generateReport({ type, from, to, organization = '' }) {
    const params = new URLSearchParams({ type, from, to });
    if (organization) params.set('organization', organization);
    return this.request(`/admin/reports?${params.toString()}`);
  }

  reportJob(id) {
    return this.request(`/admin/reports/jobs/${id}`);
  }

  // ---- Training ----
  listTrainingSessions({ status = '' } = {}) {
    return this.request(`/training${status ? `?status=${status}` : ''}`);
  }

  createTrainingSession(body) {
    return this.request('/training', { method: 'POST', body });
  }

  updateTrainingStatus(id, status) {
    return this.request(`/training/${id}/status`, { method: 'PATCH', body: { status } });
  }

  listTrainingParticipants(id) {
    return this.request(`/training/${id}/participants`);
  }

  registerTrainingParticipant(id, body) {
    return this.request(`/training/${id}/participants`, { method: 'POST', body });
  }

  saveTrainingAssessment(pid, body) {
    return this.request(`/training/participants/${pid}/assessment`, { method: 'PATCH', body });
  }

  trainingReport() {
    return this.request('/training/report');
  }

  // ---- SLA Incidents ----
  listIncidents({ status = '', severity = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (severity) params.set('severity', severity);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/incidents?${params.toString()}`);
  }

  createIncident(body) {
    return this.request('/incidents', { method: 'POST', body });
  }

  updateIncidentStatus(id, body) {
    return this.request(`/incidents/${id}/status`, { method: 'PATCH', body });
  }

  incidentMonthlyReport(month) {
    return this.request(`/incidents/report?month=${month}`);
  }

  // ---- Webhooks ----
  listWebhooks() {
    return this.request('/webhooks');
  }

  webhookInvocations() {
    return this.request('/webhooks/invocations');
  }

  createWebhook(body) {
    return this.request('/webhooks', { method: 'POST', body });
  }

  updateWebhook(id, body) {
    return this.request(`/webhooks/${id}`, { method: 'PUT', body });
  }

  testWebhook(id) {
    return this.request(`/webhooks/${id}/test`, { method: 'POST' });
  }

  deleteWebhook(id) {
    return this.request(`/webhooks/${id}`, { method: 'DELETE' });
  }

  // ---- Profile ----
  getProfile() {
    return this.request('/profile');
  }

  updateProfile(body) {
    return this.request('/profile', { method: 'PUT', body });
  }

  changePassword(body) {
    return this.request('/profile/password', { method: 'PUT', body });
  }

  // ---- Messaging ----
  approvedSenderMasks() {
    return this.request('/sender-ids/approved');
  }

  listMessages({ limit = 10, offset = 0 } = {}) {
    return this.requestWithMeta(`/messages?limit=${limit}&offset=${offset}`);
  }

  messageStats({ period = 'week' } = {}) {
    return this.request(`/messages/stats?period=${period}`);
  }

  auditActivityLogs({ from = '', to = '', email = '', role = '', event = '', limit = 25, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (email) params.set('email', email);
    if (role) params.set('role', role);
    if (event) params.set('event', event);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/audit-logs?${params.toString()}`);
  }

  auditMessages({ from = '', to = '', senderId = '', status = '', recipient = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (senderId) params.set('senderId', senderId);
    if (status) params.set('status', status);
    if (recipient) params.set('recipient', recipient);
    params.set('limit', limit);
    params.set('offset', offset);
    return this.requestWithMeta(`/messages/audit?${params.toString()}`);
  }

  sendMessage(body) {
    return this.request('/messages/send', { method: 'POST', body });
  }

  // ---- Group SMS campaigns ----
  campaignAudience({ groupIds, body }) {
    const params = new URLSearchParams();
    params.set('groupIds', groupIds.join(','));
    if (body) params.set('body', body);
    return this.request(`/campaigns/audience?${params.toString()}`);
  }

  sendCampaign(body) {
    return this.request('/campaigns/send', { method: 'POST', body });
  }

  getCampaign(id) {
    return this.request(`/campaigns/${id}`);
  }

  listCampaigns({ limit = 10, offset = 0 } = {}) {
    return this.requestWithMeta(`/campaigns?limit=${limit}&offset=${offset}`);
  }

  bulkValidateCsv({ csv, body }) {
    return this.request('/campaigns/bulk/validate', { method: 'POST', body: { csv, body } });
  }

  bulkSendCsv(body) {
    return this.request('/campaigns/bulk/send', { method: 'POST', body });
  }

  // Like request(), but returns the full envelope so callers can read `meta`.
  async requestWithMeta(endpoint, options) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...options,
    });
    const payload = await res.json();
    if (!res.ok) {
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return payload; // { data, meta }
  }
}

export default new ApiService();
